import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from '@tanstack/react-query';
import {App, Button, Empty, Modal, Skeleton, Tag} from 'antd';
import clsx from 'clsx';

import {getScopedToken, type Permission, permissionsQueryOptions} from '@services/auth';
import {useAuth} from '@hooks/useAuth';
import {useBreakpoint} from '@hooks/useBreakpoint';

import {describePermission, ROLE_ICON, ROLE_LABEL} from './PermissionPickerModal.utils';

import styles from './PermissionPickerModal.module.scss';

type PermissionPickerModalProps = {
  open: boolean;
  onClose: () => void;
};

export const PermissionPickerModal = ({open, onClose}: PermissionPickerModalProps) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const {isMobile} = useBreakpoint();
  const {message} = App.useApp();

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const {data: permissions, isLoading} = useQuery({
    ...permissionsQueryOptions(),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: getScopedToken,
    onError: (err) => {
      message.error(err instanceof Error ? err.message : 'Не удалось применить доступ');
    },
  });

  const applyPermission = (permission: Permission) => {
    mutation.mutate(
      {permissionId: permission.id},
      {
        onSuccess: ({token}) => {
          auth.setToken(token);
          auth.selectPermission(permission);
          onClose();
          navigate('/', {replace: true});
        },
      },
    );
  };

  // Авто-пик если permission единственный.
  useEffect(() => {
    if (!open || !permissions || permissions.length !== 1 || mutation.isPending) return;
    const only = permissions[0];
    if (!only) return;
    applyPermission(only);
    // applyPermission в deps не нужен — это event-like, идемпотентный по permissions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, permissions]);

  // Сброс выбора при закрытии.
  useEffect(() => {
    if (!open) setSelectedId(null);
  }, [open]);

  const handleConfirm = () => {
    if (selectedId === null || !permissions) return;
    const picked = permissions.find((p) => p.id === selectedId);
    if (!picked) return;
    applyPermission(picked);
  };

  const handleCancel = () => {
    if (mutation.isPending) return;
    if (!auth.permission) auth.logout();
    onClose();
  };

  return (
    <Modal
      open={open}
      title={
        <div className={styles.title}>
          <div className={styles.titleText}>Выберите доступ</div>
          <div className={styles.titleHint}>
            У вас несколько доступов — выберите, в каком работаем сейчас.
          </div>
        </div>
      }
      onCancel={handleCancel}
      maskClosable={false}
      width={isMobile ? '94%' : 540}
      style={isMobile ? {top: 16} : undefined}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={mutation.isPending}>
          Отмена
        </Button>,
        <Button
          key="ok"
          type="primary"
          onClick={handleConfirm}
          loading={mutation.isPending}
          disabled={selectedId === null}
        >
          Продолжить
        </Button>,
      ]}
    >
      {isLoading ? (
        <div className={styles.list}>
          <Skeleton active avatar paragraph={{rows: 1}} />
          <Skeleton active avatar paragraph={{rows: 1}} />
        </div>
      ) : !permissions || permissions.length === 0 ? (
        <Empty description="Нет доступов" />
      ) : (
        <div className={styles.list}>
          {permissions.map((p) => (
            <PermissionCard
              key={p.id}
              permission={p}
              selected={selectedId === p.id}
              onSelect={setSelectedId}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

/* ─── Card ───────────────────────────────────────────────────────── */

type PermissionCardProps = {
  permission: Permission;
  selected: boolean;
  onSelect: (id: number) => void;
};

const PermissionCard = ({permission, selected, onSelect}: PermissionCardProps) => {
  const parts = describePermission(permission);

  const handleClick = () => {
    onSelect(permission.id);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={clsx(styles.card, selected && styles.cardSelected)}
    >
      <div className={styles.avatar}>{ROLE_ICON[permission.role]}</div>
      <div className={styles.body}>
        <div className={styles.roleRow}>
          <span className={styles.roleName}>{ROLE_LABEL[permission.role] ?? permission.role}</span>
          <Tag className={styles.idTag}>#{permission.id}</Tag>
          {permission.percent !== null && permission.percent !== undefined && (
            <Tag className={styles.percentTag}>{permission.percent}%</Tag>
          )}
        </div>
        {parts.length > 0 ? (
          <div className={styles.description}>{parts.join(' · ')}</div>
        ) : (
          <div className={styles.descriptionEmpty}>без привязок</div>
        )}
      </div>
      <div className={styles.radio} />
    </button>
  );
};
